// Direct Customer Display -> backend submission for Service Rating. No staff
// JWT, no document id, no branch/company id: authorized solely by the
// scoped, single-use rating_token the cashier's tab already fetched via
// evaluation_session/get_or_create and handed down through the sanitized
// protocol (see protocol.js's sanitizeRatingSession). Framework-independent
// and pure except for the injected fetch, so it is unit-testable without a
// real network call, and is exactly the surface a future non-browser device
// (ESP32) would call the same way over plain HTTP - nothing here is
// browser-specific beyond the optional AbortController timeout.

const RATING_SESSION_STATUSES = ['waiting', 'rated', 'timeout', 'cancelled_by_new_document', 'closed_without_payment'];
const RATING_VALUES = [1, 2, 3];

function str(value, max) {
  if (value === null || value === undefined) return '';
  return String(value).trim().slice(0, max);
}

// Mirrors protocol.js's own allow-list exactly: only id/ratingToken/status/
// expiresAt/rating ever leave this mapping step. The raw backend response
// also carries document_id and an echoed rating_token - neither may linger in
// any caller's state beyond this function. rating (1/2/3) is the customer's
// own just-made choice, not sensitive, and lets a reconciled ALREADY_RATED
// result show the actually-confirmed face rather than assuming the tap that
// lost the race is accurate.
export function mapSubmitRatingResult(result) {
  if (!result || typeof result !== 'object') return null;
  const id = str(result._id, 64);
  const ratingToken = str(result.rating_token, 128);
  if (!id || !ratingToken || RATING_SESSION_STATUSES.indexOf(result.status) === -1) return null;
  const rating = Number(result.rating);
  return {
    id,
    ratingToken,
    status: result.status,
    expiresAt: str(result.expires_at, 32),
    rating: RATING_VALUES.indexOf(rating) === -1 ? null : rating
  };
}

export const OUTCOME = {
  SUCCESS: 'success', // rated just now; use .session
  ALREADY_RATED: 'already-rated', // lost a race, but the backend confirms status === 'rated'
  EXPIRED: 'expired', // terminal/unratable: timeout, closed, cancelled, or an unreadable 409
  INVALID: 'invalid', // bad session/token, or a malformed request (400/403)
  NETWORK_ERROR: 'network-error' // fetch failed, timed out, or the response could not be parsed
};

const DEFAULT_TIMEOUT_MS = 8000;

// deviceType is forward-compatible with a future non-browser device (ESP32);
// 'customer_display' today is accepted by the backend but not yet persisted.
export async function submitRating({
  baseUrl,
  sessionId,
  ratingToken,
  rating,
  deviceType = 'customer_display',
  fetchImpl = fetch,
  timeoutMs = DEFAULT_TIMEOUT_MS
}) {
  // The timeout must stay live through the whole request, not just until
  // fetch's headers resolve - a stalled response body (slow/hung stream)
  // must also be aborted, or a customer-safe NETWORK_ERROR would never
  // arrive and the display would hang on "submitting" forever. One outer
  // try/catch/finally covers fetch, body parsing and classification, and the
  // timer is only ever cleared at the very end.
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
  try {
    const response = await fetchImpl(`${baseUrl}/evaluation_session/submit_rating`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_id: sessionId,
        rating_token: ratingToken,
        rating,
        device_type: deviceType
      }),
      signal: controller ? controller.signal : undefined
    });
    // Parsing the body is isolated from the rest of classification: a
    // malformed/empty body for a TERMINAL status (e.g. a 401/404/410 with
    // no body, or one that isn't valid JSON) must still classify by that
    // status code below, not be swept into the generic NETWORK_ERROR catch
    // below it - previously response.json() ran unconditionally before any
    // status check, so a parse failure on ANY status fell straight into the
    // outer catch's NETWORK_ERROR regardless of what the status actually
    // was, incorrectly turning a terminal/non-retryable 4xx into a RETRY.
    let body = null;
    try {
      body = await response.json();
    } catch (parseError) {
      body = null;
    }
    const session = mapSubmitRatingResult(body && body.result);
    // A SUCCESS or ALREADY_RATED result is used to confirm a rating actually
    // stuck, and feeds a completion acknowledgement to the publisher (and,
    // for the RP2040 rating controller, an ACK:<rating> LED/sound command -
    // see CustomerDisplayPage.vue) - so before trusting either, the returned
    // session must demonstrably be:
    //   - the one this request submitted (same session id, and the same
    //     scoped token if the backend echoed one back);
    //   - actually in the 'rated' status, not merely well-formed;
    //   - carrying back the SAME rating (1-3) this request submitted, not a
    //     different one (mapSubmitRatingResult already normalizes anything
    //     outside 1-3 to null, which fails this comparison too).
    // A "success" response that fails any of this is never used to confirm
    // anything - never rendered or logged, only compared.
    const identityMatches = Boolean(session) && session.id === sessionId && session.ratingToken === ratingToken;
    const isValidRatedResult = identityMatches && session.status === 'rated' && session.rating === rating;
    if (response.status === 200 && body && body.status === 'success' && session) {
      return isValidRatedResult ? { outcome: OUTCOME.SUCCESS, session } : { outcome: OUTCOME.NETWORK_ERROR, session: null };
    }
    if (response.status === 409) {
      // already_closed: the session is either freshly rated (a race this tap
      // lost) or genuinely terminal (timeout/closed/cancelled) - the backend's
      // own current status is the only thing that tells the two apart. Unlike
      // the 200/success path above, a race winner's rating need not match
      // what THIS request submitted (identityMatches is still required, but
      // not isValidRatedResult's rating-equality - that would wrongly reject
      // a legitimate "someone else's tap already set it to a different
      // value" race outcome).
      if (session && session.status === 'rated') {
        return identityMatches ? { outcome: OUTCOME.ALREADY_RATED, session } : { outcome: OUTCOME.NETWORK_ERROR, session: null };
      }
      return { outcome: OUTCOME.EXPIRED, session };
    }
    // Terminal 4xx per the backend contract: the request was understood but
    // can never succeed by retrying, so these must never become RETRY on the
    // RP2040 (requirement: restrict RETRY to genuinely retryable failures).
    // 400/401/403/422 are rejected/invalid requests (bad payload, bad/expired
    // rating_token, unprocessable value); 404/410 mean the session itself no
    // longer exists to rate.
    if (response.status === 404 || response.status === 410) {
      return { outcome: OUTCOME.EXPIRED, session };
    }
    // Any other 4xx is terminal except request-timeout/rate-limit. This also
    // handles a proxy or future backend returning 405/413/415 without turning
    // a permanently rejected request into an endless RETRY loop.
    if (response.status >= 400 && response.status < 500 && response.status !== 408 && response.status !== 429) {
      return { outcome: OUTCOME.INVALID, session };
    }
    // Genuinely retryable: a rate limit (429) or a transient server error
    // (5xx) - both are the kind of failure where pressing the button again
    // might actually succeed, which is what NETWORK_ERROR/RETRY means to the
    // customer and to the RP2040 controller alike. Anything else unexpected
    // and unlisted also falls back here, unchanged from before.
    return { outcome: OUTCOME.NETWORK_ERROR, session };
  } catch (error) {
    // Covers only a thrown/rejected fetch itself (network failure, or the
    // timeout abort above firing mid-request/mid-body-read) - an
    // unparseable response body no longer reaches here, see the body-parse
    // try/catch above, which lets a terminal status still classify
    // correctly even with a bad body.
    return { outcome: OUTCOME.NETWORK_ERROR, session: null };
  } finally {
    if (timer) clearTimeout(timer);
  }
}
